import type { Operation } from '@apollo/client';
import { ApolloLink, Observable } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { Kind, OperationTypeNode } from 'graphql';

const isSubscription = (operation: Operation) =>
  !!operation.query.definitions.find(
    (d) =>
      d.kind === Kind.OPERATION_DEFINITION &&
      d.operation == OperationTypeNode.SUBSCRIPTION
  );

const isMutation = (operation: Operation) =>
  !!operation.query.definitions.find(
    (d) =>
      d.kind === Kind.OPERATION_DEFINITION &&
      d.operation == OperationTypeNode.MUTATION
  );

// Sentry span status: code 0 = unset (defaults to ok), 2 = error.
const SPAN_STATUS_ERROR = { code: 2 as const, message: 'internal_error' };

/**
 * RSP-6971: one Sentry span per (non-subscription) GraphQL operation covering
 * the FULL request cycle — dispatch → response OR error.
 *
 * Child span (`startInactiveSpan`), so it nests under the active pageload /
 * navigation transaction and is rendered when such a transaction is live. We
 * own its whole lifecycle here and close it on EVERY outcome:
 *
 * - success                 → span ends, status ok
 * - GraphQL errors (`next`)  → status error, span ends on `complete`
 * - network error (`error`)  → status error, span ends
 * - unsubscribe / abort      → span ends (cancelled)
 *
 * The previous implementation ended the span only when a result was delivered
 * (`logTimeLink`'s `.map`), so a network-error rejection (no `next`) leaked the
 * span and recorded neither duration nor error status — exactly the failure
 * case we want visible.
 */
const apolloMetricLink = new ApolloLink((operation, forward) => {
  if (isSubscription(operation)) {
    return forward(operation);
  }

  const querySpan = Sentry.startInactiveSpan({
    name: operation.operationName || 'anonymous',
    op: isMutation(operation) ? 'http.graphql.mutation' : 'http.graphql.query',
  });
  operation.setContext({ start: performance.now(), querySpan });

  // End the span exactly once, whichever outcome arrives first.
  let ended = false;
  const endSpan = () => {
    if (ended) {
      return;
    }
    ended = true;
    querySpan.end();
  };

  return new Observable((observer) => {
    const subscription = forward(operation).subscribe({
      next: (result) => {
        if (result.errors?.length) {
          querySpan.setStatus(SPAN_STATUS_ERROR);
        }
        observer.next(result);
      },
      error: (error) => {
        querySpan.setStatus(SPAN_STATUS_ERROR);
        endSpan();
        observer.error(error);
      },
      complete: () => {
        endSpan();
        observer.complete();
      },
    });

    // Runs on unsubscribe (component unmount / query cancellation) as well as
    // after complete/error — guarded by `ended` so the span closes once.
    return () => {
      endSpan();
      subscription.unsubscribe();
    };
  });
});

export default apolloMetricLink;
